import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { JsonReadonlyBlock } from '#components/json-readonly-block/json-readonly-block';

const EVENT_PROCESSOR_RUN_INPUT = 'processor_run_input';
const EVENT_PROCESSOR_RUN_OUTPUT = 'processor_run_output';
const EVENT_PROCESSOR_LOG = 'processor_log';
const EVENT_AI_AUDIT = 'ai_audit';
const EVENT_AI_AUDIT_INPUT = 'ai_audit_input';
const EVENT_AI_AUDIT_OUTPUT = 'ai_audit_output';
const EVENT_MIDDLE_TRUNCATED = 'processor_run_middle_truncated';
const EVENT_BUFFER_TRUNCATED = 'processor_run_log_buffer_truncated';

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;

  return `${s.slice(0, max - 1)}…`;
}

function summarizeEntry(obj: Record<string, unknown>): string {
  const event = typeof obj.event === 'string' ? obj.event : 'unknown_event';

  switch (event) {
    case EVENT_PROCESSOR_RUN_INPUT: {
      const pid = typeof obj.processorId === 'string' ? obj.processorId : '—';
      const iid = typeof obj.instanceId === 'string' ? obj.instanceId : '—';
      return `Input · processor ${pid} · instance ${truncate(iid, 36)}`;
    }
    case EVENT_PROCESSOR_RUN_OUTPUT: {
      if (obj.success === true) {
        return 'Output · success';
      }

      const err = typeof obj.error === 'string' ? obj.error : 'failed';
      return `Output · error: ${truncate(err, 120)}`;
    }
    case EVENT_PROCESSOR_LOG: {
      const level =
        typeof obj.processorLogLevel === 'string'
          ? obj.processorLogLevel
          : 'log';
      const msg =
        typeof obj.message === 'string'
          ? obj.message
          : typeof obj.msg === 'string'
            ? obj.msg
            : '';
      return msg ? `[${level}] ${truncate(msg, 160)}` : `[${level}]`;
    }
    case EVENT_AI_AUDIT:
    case EVENT_AI_AUDIT_INPUT:
    case EVENT_AI_AUDIT_OUTPUT: {
      const ep = typeof obj.endpoint === 'string' ? obj.endpoint : '—';
      const meta = obj.metadata;
      let model = '';
      if (isRecord(meta) && typeof meta.model === 'string') {
        model = ` · ${meta.model}`;
      }

      const phase =
        event === EVENT_AI_AUDIT_INPUT
          ? 'Input'
          : event === EVENT_AI_AUDIT_OUTPUT
            ? 'Output'
            : 'Audit';
      return `${phase} · ${ep}${model}`;
    }
    case EVENT_MIDDLE_TRUNCATED:
    case EVENT_BUFFER_TRUNCATED: {
      const m = typeof obj.message === 'string' ? obj.message : 'Truncated';
      return truncate(m, 160);
    }
    default: {
      const msg =
        typeof obj.msg === 'string'
          ? obj.msg
          : typeof obj.message === 'string'
            ? obj.message
            : '';
      if (msg) return truncate(msg, 160);

      try {
        return truncate(JSON.stringify(obj), 120);
      } catch {
        return event;
      }
    }
  }
}

function eventBadgePalette(
  event: string,
  obj: Record<string, unknown>,
): 'blue' | 'green' | 'red' | 'gray' | 'purple' | 'yellow' {
  switch (event) {
    case EVENT_PROCESSOR_RUN_INPUT:
      return 'blue';
    case EVENT_PROCESSOR_RUN_OUTPUT:
      return obj.success === true ? 'green' : 'red';
    case EVENT_PROCESSOR_LOG:
      return 'gray';
    case EVENT_AI_AUDIT:
    case EVENT_AI_AUDIT_INPUT:
    case EVENT_AI_AUDIT_OUTPUT:
      return 'purple';
    case EVENT_MIDDLE_TRUNCATED:
    case EVENT_BUFFER_TRUNCATED:
      return 'yellow';
    default:
      return 'gray';
  }
}

function formatTime(obj: Record<string, unknown>): string | null {
  if (typeof obj.time === 'string' && obj.time.length > 0) {
    return obj.time;
  }

  return null;
}

export interface ProcessorInstanceLogViewerProps {
  instanceId: string;
  apiHost: string;
}

export function ProcessorInstanceLogViewer({
  instanceId,
  apiHost,
}: ProcessorInstanceLogViewerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<
    { index: number; value: Record<string, unknown> }[]
  >([]);
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

  const url = useMemo(
    () =>
      `${apiHost}/api/processor-instances/${encodeURIComponent(instanceId)}/run-log`,
    [apiHost, instanceId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        setError(
          res.status === 404
            ? 'Run log not found.'
            : `Failed to load run log (${res.status}).`,
        );
        setEntries([]);
        return;
      }

      const text = await res.text();
      const lines = text.split('\n');
      const parsed: { index: number; value: Record<string, unknown> }[] = [];
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (line.trim() === '') continue;

        try {
          const raw: unknown = JSON.parse(line);
          if (isRecord(raw)) {
            parsed.push({ index: i, value: raw });
          } else {
            parsed.push({
              index: i,
              value: {
                event: 'non_object_line',
                value: raw,
              },
            });
          }
        } catch {
          parsed.push({
            index: i,
            value: {
              event: 'invalid_json',
              rawLine: line,
            },
          });
        }
      }
      setEntries(parsed);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setError(message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleExpanded = useCallback((index: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }

      return next;
    });
  }, []);

  if (loading) {
    return (
      <VStack gap={2} align="stretch">
        <Heading size="sm" color="fg">
          Run log
        </Heading>
        <HStack gap={2}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="fg.muted">
            Loading run log…
          </Text>
        </HStack>
      </VStack>
    );
  }

  if (error) {
    return (
      <VStack gap={2} align="stretch">
        <Heading size="sm" color="fg">
          Run log
        </Heading>
        <Text fontSize="sm" color="red.500">
          {error}
        </Text>
        <Button
          size="xs"
          variant="outline"
          alignSelf="flex-start"
          onClick={load}
        >
          Retry
        </Button>
      </VStack>
    );
  }

  if (entries.length === 0) {
    return (
      <VStack gap={2} align="stretch">
        <Heading size="sm" color="fg">
          Run log
        </Heading>
        <Text fontSize="sm" color="fg.muted">
          No log lines in this run.
        </Text>
      </VStack>
    );
  }

  return (
    <VStack gap={3} align="stretch">
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <Heading size="sm" color="fg">
          Run log
        </Heading>
        <Button size="xs" variant="outline" onClick={() => void load()}>
          Refresh
        </Button>
      </HStack>
      <Text fontSize="xs" color="fg.muted">
        NDJSON events from this processor run (newest lines may appear last).
      </Text>
      <VStack gap={2} align="stretch">
        {entries.map(({ index, value }) => {
          const eventStr =
            typeof value.event === 'string' ? value.event : 'unknown';
          const palette = eventBadgePalette(eventStr, value);
          const summary = summarizeEntry(value);
          const timeStr = formatTime(value);
          const isOpen = expanded.has(index);

          return (
            <Box
              key={`${index}-${eventStr}`}
              borderWidth="1px"
              borderRadius="md"
              borderColor="border"
              overflow="hidden"
              bg="bg.subtle"
            >
              <HStack gap={3} align="flex-start" px={3} py={2} flexWrap="wrap">
                <Badge size="sm" colorPalette={palette}>
                  {eventStr}
                </Badge>
                {timeStr ? (
                  <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap">
                    {timeStr}
                  </Text>
                ) : null}
                <Text fontSize="sm" color="fg" flex="1" minW={0}>
                  {summary}
                </Text>
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  flexShrink={0}
                  onClick={() => toggleExpanded(index)}
                  aria-expanded={isOpen}
                >
                  {isOpen ? 'Hide full payload' : 'Show full payload'}
                </Button>
              </HStack>
              {isOpen ? (
                <Box px={3} pb={3} borderTopWidth="1px" borderColor="border">
                  <JsonReadonlyBlock
                    title="Payload"
                    value={value}
                    fitContentHeight
                  />
                </Box>
              ) : null}
            </Box>
          );
        })}
      </VStack>
    </VStack>
  );
}
